export type * from './repository'
export { emptyApplicationLinks } from './architecture-links'
import { databaseArchRepo, databaseBizRepo, databaseTechRepo, databaseModelRepo } from './architecture-database'
import { httpProductImpl, httpArchImpl, httpOpsImpl, httpDevImpl, httpSpecImpl } from './http-backend'
export { fetchArchAssetCounts, fetchArchTableCounts, fetchDomainRelations, fetchArchitectHealth } from './http-backend'
export type { ArchAssetCounts } from './http-backend'

/**
 * 架构页统一门面
 * ------------------------------------------------------------
 * - archRepo：4A 数据库快照（/api/arch/v1/page）为基座，REST 端点补齐快照未覆盖的派生接口；
 *   话题讨论群节点是本机选择态（无后端资源），余下全部来自后端。
 * - bizRepo / modelRepo：数据库只读适配器（不接受的方法直接报错，不回退 mock）。
 * - productRepo：数据库HTTP接口，不读取本机mock台账。
 * - opsRepo / devRepo / specRepo：纯 REST 直连，后端不可用时由视图显式提示。
 */
export const itemRev = (item: {rev?: import('./repository').SpecRev}): import('./repository').SpecRev => item.rev || '已通过'

/* ---------- 门面方法签名（数据库只读适配器的消费侧契约；未列方法经 Proxy 显式拒绝） ---------- */
import type {
  ObjType, RelType, NavBinding, NumberPrefix, C0Field, RelColorLegend, RelInst, ResolvedEnd,
  ValueStream, BizRule, BizVal, BizGoal, BizDomain, BizRole, StateMachine, BizEvent,
  NetLayer, NetZone, Env, DeploySvc, SwLayer
} from './repository'

/** 元模型字典快照（modelRepo.modelMeta 返回形状） */
export interface ModelMetaSnapshot {
  objTypes: ObjType[]
  relTypes: RelType[]
  navBindings: NavBinding[]
  numberPrefixes: NumberPrefix[]
  c0Fields: C0Field[]
  labels: {
    lifecycle: Record<string, string>
    authority: Record<string, string>
    assertion: Record<string, string>
    coverage: Record<string, string>
    layer: Record<string, any>
    tier: Record<string, any>
  }
  colorLegend: RelColorLegend[]
  navViewLabels: Record<string, string>
}
/** 关系实例 + 两端解析结果（详情抽屉出入边行） */
export interface ModelRelEdge { rel: RelInst & { endpointStatus?: string }; src: ResolvedEnd; tgt: ResolvedEnd }
export interface ModelRelStats {
  total: number
  byType: Record<string, number>
  byAm: Record<string, number>
  byCs: Record<string, number>
  broken: { endpoint: string; count: number }[]
}
export interface ModelObjectDetail {
  type: ObjType | undefined
  item: any
  end: ResolvedEnd & { item?: any }
  apiFields: any[] | null
  c0: { field: C0Field; value: any }[]
  outRels: ModelRelEdge[]
  inRels: ModelRelEdge[]
}
export interface ModelNavResolution {
  binding: NavBinding | undefined
  code: string
  view: string
  go: string
  pending: boolean
  label: string
}
interface ModelRepoFacade {
  modelMeta(): Promise<ModelMetaSnapshot>
  typeStats(): Promise<Record<string, number>>
  relStats(): Promise<ModelRelStats>
  relInstances(): Promise<RelInst[]>
  endpointsOf(type: string): Promise<{ rt: RelType | undefined; rows: ModelRelEdge[] }>
  typeSamples(type: string, limit?: number): Promise<{ ref: string; name: string }[]>
  objectDetail(type: string, ref: string): Promise<ModelObjectDetail>
  resolveNav(type: string, ref: string): Promise<ModelNavResolution>
  [method: string]: any
}
interface BizRepoFacade {
  valueStreams(): Promise<ValueStream[]>
  bizRules(): Promise<BizRule[]>
  bizVals(): Promise<BizVal[]>
  goals(): Promise<BizGoal[]>
  bizDomains(): Promise<BizDomain[]>
  bizRoles(): Promise<BizRole[]>
  stateMachines(): Promise<StateMachine[]>
  bizEvents(): Promise<BizEvent[]>
  [method: string]: any
}
interface TechRepoFacade {
  netLayers(): Promise<NetLayer[]>
  netZones(): Promise<NetZone[]>
  envs(): Promise<Env[]>
  deploySvcs(): Promise<DeploySvc[]>
  swLayers(): Promise<SwLayer[]>
}
/** 架构快照门面：indexes 为基座聚合，其余方法面大且按页签分散，保留开放索引 */
interface ArchRepoFacade {
  indexes(): Promise<any>
  [method: string]: any
}

/* ---------- 架构快照 + REST 派生接口 ---------- */
const ROOM_NODE_KEY = 'ema.room.node.v1'
/** 快照适配器未覆盖、由 REST 端点提供的派生接口 */
const ARCH_REST_METHODS = ['exOrgRows', 'ndOrgRows', 'roomModel', 'exOfDom', 'exOfEp', 'wbKpis', 'wbItems', 'wbSay', 'wbArch', 'mDomBars', 'mEpBars'] as const
const archRest: Record<string, any> = {}
for (const k of ARCH_REST_METHODS) archRest[k] = (httpArchImpl as any)[k]

function databaseOnly<T extends Record<string, any>>(methods: T): T {
  return new Proxy(methods, { get(target, key: string) {
    if (key in target) return (target as any)[key]
    return () => Promise.reject(new Error(`4A 数据库只读接口尚不支持 ${String(key)}；未回退 mock。`))
  } }) as T
}

export const archRepo: ArchRepoFacade = databaseOnly({
  ...(databaseArchRepo as any),
  ...archRest,
  /** 话题讨论群当前业务节点：本机选择态；未选择时取台账首个节点 */
  setRoomNode(c: string) { try { localStorage.setItem(ROOM_NODE_KEY, c) } catch { /* 忽略 */ } },
  async roomNode(): Promise<string> {
    let saved = ''
    try { saved = localStorage.getItem(ROOM_NODE_KEY) || '' } catch { /* 忽略 */ }
    if (saved) return saved
    const idx = await (databaseArchRepo as any).indexes()
    return idx?.nds?.[0]?.code || ''
  }
})

export const bizRepo: BizRepoFacade = databaseOnly(databaseBizRepo as any)
export const techRepo: TechRepoFacade = databaseOnly(databaseTechRepo as any)
export const modelRepo: ModelRepoFacade = databaseOnly(databaseModelRepo as any)

/** 产品台账沿已有数据库HTTP接口读取；失败不回退本机样例。 */
export const productRepo = httpProductImpl
export const opsRepo = httpOpsImpl
export const specRepo = httpSpecImpl
export const devRepo = httpDevImpl
